// =============================================
// Child Avatar Component with Fallback
// Displays child photo or placeholder with initials
// =============================================

import { useState } from 'react';
import { User } from 'lucide-react';

interface ChildAvatarProps {
  photoUrl?: string | null;
  firstName: string;
  lastName?: string;
  size?: number;
  borderRadius?: number | string;
  borderColor?: string;
  borderWidth?: number;
  className?: string;
  style?: React.CSSProperties;
}

// Generate a consistent color based on name
const getColorFromName = (name: string): string => {
  const colors = [
    '#f87171', // red
    '#fb923c', // orange
    '#fbbf24', // amber
    '#a3e635', // lime
    '#4ade80', // green
    '#2dd4bf', // teal
    '#22d3ee', // cyan
    '#60a5fa', // blue
    '#a78bfa', // violet
    '#f472b6', // pink
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  return colors[Math.abs(hash) % colors.length];
};

export default function ChildAvatar({
  photoUrl,
  firstName,
  lastName = '',
  size = 48,
  borderRadius = '50%',
  borderColor,
  borderWidth = 0,
  className,
  style,
}: ChildAvatarProps) {
  const [imageError, setImageError] = useState(false);

  const fullName = `${firstName} ${lastName}`.trim();
  const initials = `${firstName.charAt(0)}${lastName ? lastName.charAt(0) : ''}`;
  const bgColor = getColorFromName(fullName);

  const containerStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: borderWidth > 0 ? `${borderWidth}px solid ${borderColor || '#e5e7eb'}` : undefined,
    ...style,
  };

  const hasValidPhoto = photoUrl && !imageError;

  if (hasValidPhoto) {
    return (
      <div style={containerStyle} className={className}>
        <img
          src={photoUrl}
          alt={fullName}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Fallback: colored circle with initials or icon
  return (
    <div
      style={{
        ...containerStyle,
        backgroundColor: bgColor,
        color: 'white',
        fontSize: size * 0.4,
        fontWeight: 600,
      }}
      className={className}
    >
      {initials ? (
        <span>{initials}</span>
      ) : (
        <User size={size * 0.5} />
      )}
    </div>
  );
}
